import { _ as _extends } from '../../_rollupPluginBabelHelpers-c245b26a.js';
import { forwardRef, useRef } from 'react';
import { ListboxProvider } from './useListboxContext.js';
import { mergeRefs } from '../../utils/mergeRefs.js';
import { useListbox } from './useListbox.js';
import { jsx } from '@emotion/react';
import { InteractiveList } from '../InteractiveList/InteractiveList.js';
import './useSelectableCollectionKeyboard.js';
import './useTypeKeySearch.js';
import '../../hooks/useCollection.js';
import '../../hooks/useControllableState.js';
import '../../utils/warn.js';
import '@indeed/ifl-css';

/**
 * @see https://experience.indeed.design/components/ids/listbox
 */
const Listbox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    defaultSelectedValues,
    selectedValues,
    onSelectionChange,
    selectionType = "single",
    sx,
    ...remainingProps
  } = props;
  const multiselect = selectionType === "multiple";
  const listboxRef = useRef(null);
  const {
    activeOption,
    setActiveOption,
    selectOption,
    selectedOptionValues,
    focusVisible,
    setFocusVisible,
    initializeVirtualFocus,
    onKeyDown,
    onKeyDownCapture,
    collectionState
  } = useListbox({
    scrollContainerRef: listboxRef,
    defaultSelectedValues,
    selectedValues,
    onSelectionChange,
    multiselect
  });
  const handleOnKeyDown = event => {
    onKeyDown(event);
    remainingProps.onKeyDown == null || remainingProps.onKeyDown(event);
  };
  const handleOnKeyDownCapture = event => {
    onKeyDownCapture(event);
    remainingProps.onKeyDownCapture == null || remainingProps.onKeyDownCapture(event);
  };
  const handleOnFocus = event => {
    setFocusVisible(true);
    if (event.target === listboxRef.current) {
      initializeVirtualFocus();
    }
    remainingProps.onFocus == null || remainingProps.onFocus(event);
  };
  const handleOnBlur = event => {
    setActiveOption(undefined);
    remainingProps == null || remainingProps.onBlur == null || remainingProps.onBlur(event);
  };
  const context = {
    selectionType,
    activeOption,
    setActiveOption,
    selectOption,
    selectedOptionValues,
    registerOption: collectionState.registerItem,
    focusVisible,
    setFocusVisible
  };
  return jsx(ListboxProvider, {
    value: context
  }, jsx(InteractiveList, _extends({
    ref: mergeRefs(ref, listboxRef),
    role: multiselect ? "menu" : "listbox",
    tabIndex: 0,
    "aria-activedescendant": activeOption ? activeOption.id : undefined,
    "aria-multiselectable": multiselect || undefined
  }, remainingProps, {
    onKeyDown: handleOnKeyDown,
    onKeyDownCapture: handleOnKeyDownCapture,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    sx: {
      "&:focus": {
        outline: "none"
      },
      ...sx
    }
  }), children));
});
Listbox.displayName = "Listbox";

export { Listbox };
